import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Card, Row, Col, Statistic, Table, Tag, Button, Spin, Empty, Space, Progress, Typography,
} from 'antd';
import {
    ArrowLeftOutlined,
    UserOutlined,
    BarChartOutlined,
    HistoryOutlined,
    LineChartOutlined,
    PlayCircleOutlined,
    QuestionCircleOutlined,
    MessageOutlined,
    DownloadOutlined,
} from '@ant-design/icons';
import { getCourse, getCourseAnalytics, getCourseTopLearners } from '../services/api';
import { capturePageAsHTML } from '../utils/exportHTML';

const { Title, Text } = Typography;

// Helper to get icon by category
const getCategoryIcon = (category) => {
    switch (category) {
        case 'video': return <PlayCircleOutlined style={{ color: '#1890ff' }} />;
        case 'problem': return <QuestionCircleOutlined style={{ color: '#52c41a' }} />;
        case 'forum': return <MessageOutlined style={{ color: '#faad14' }} />;
        case 'navigation': return <LineChartOutlined style={{ color: '#eb2f96' }} />;
        case 'enrollment': return <UserOutlined style={{ color: '#722ed1' }} />;
        default: return <BarChartOutlined />;
    }
};

// Simple Sparkline component using SVG
const ActivitySparkline = ({ data, color = '#1890ff' }) => {
    if (!data || data.length < 2) return null;

    const max = Math.max(...data.map(d => parseInt(d.total)), 1);
    const width = 400;
    const height = 100;
    const padding = 10;

    const points = data.map((d, i) => {
        const x = padding + (i * (width - 2 * padding) / (data.length - 1));
        const y = (height - padding) - (parseInt(d.total) * (height - 2 * padding) / max);
        return `${x},${y}`;
    }).join(' ');

    return (
        <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" style={{ background: '#f5f5f5', borderRadius: 8 }}>
            <polyline
                fill="none"
                stroke={color}
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                points={points}
            />
        </svg>
    );
};

export default function CourseAnalyticsPage() {
    const { courseKey } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [learnersLoading, setLearnersLoading] = useState(true);
    const [course, setCourse] = useState(null);
    const [analytics, setAnalytics] = useState(null);
    const [topLearners, setTopLearners] = useState([]);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                // Phase 1: fast queries (daily + category) — loads page immediately
                const [courseRes, analyticsRes] = await Promise.all([
                    getCourse(courseKey),
                    getCourseAnalytics(courseKey)
                ]);
                setCourse(courseRes.data.data);
                setAnalytics(analyticsRes.data.data);
                setLoading(false);

                // Phase 2: heavy learner query — loads after page is visible
                setLearnersLoading(true);
                const learnersRes = await getCourseTopLearners(courseKey, 100);
                setTopLearners(learnersRes.data.data || []);
            } catch (error) {
                console.error('Failed to fetch analytics', error);
                setLoading(false);
            } finally {
                setLearnersLoading(false);
            }
        };
        fetchData();
    }, [courseKey]);

    if (loading) return <div style={{ textAlign: 'center', padding: '100px' }}><Spin size="large" /></div>;
    if (!course || !analytics) return <Empty description="Data not found" />;

    const learnerColumns = [
        {
            title: 'Username',
            dataIndex: 'username',
            key: 'username',
            render: (text) => <Space><UserOutlined /> {text || 'Anonymous'}</Space>
        },
        {
            title: 'Video',
            dataIndex: 'video_play',
            render: (v) => v > 0 ? <Tag color="blue">{v}</Tag> : '-'
        },
        {
            title: 'Prob',
            dataIndex: 'prob_check',
            render: (v) => v > 0 ? <Tag color="green">{v}</Tag> : '-'
        },
        {
            title: 'Forum',
            dataIndex: 'forum_post',
            render: (v) => v > 0 ? <Tag color="purple">{v}</Tag> : '-'
        },
        {
            title: 'Nav',
            dataIndex: 'nav_jump',
            render: (v) => v > 0 ? <Tag color="pink">{v}</Tag> : '-'
        },
        {
            title: 'Total',
            dataIndex: 'total_events',
            key: 'total_events',
            sorter: (a, b) => a.total_events - b.total_events,
            render: (val) => <Text strong>{parseInt(val).toLocaleString()}</Text>
        }
    ];

    return (
        <div style={{ paddingBottom: 40 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 24 }}>
                <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/courses')} data-export-hide>
                    Back to Courses
                </Button>
                <Button
                    icon={<DownloadOutlined />}
                    onClick={() => capturePageAsHTML(`analytics_${course.display_name || course.course_id}`)}
                    disabled={!analytics || learnersLoading}
                    data-export-hide
                >
                    Export HTML
                </Button>
            </div>

            <Row gutter={[24, 24]} align="middle" style={{ marginBottom: 32 }}>
                <Col span={24}>
                    <Title level={2} style={{ margin: 0 }}>
                        <BarChartOutlined /> Analytics: {course.display_name || course.course_id}
                    </Title>
                    <Text type="secondary">Detailed student interaction insights and activity patterns</Text>
                </Col>
            </Row>

            {/* Overview Cards */}
            <Row gutter={[24, 24]} style={{ marginBottom: 24 }}>
                <Col xs={24} sm={12} lg={6}>
                    <Card bordered={false} className="analytics-card">
                        <Statistic
                            title="Total Interactions"
                            value={course.stats?.total_events}
                            prefix={<HistoryOutlined />}
                            valueStyle={{ color: '#1890ff' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card bordered={false} className="analytics-card">
                        <Statistic
                            title="Unique Learners"
                            value={course.stats?.total_learners}
                            prefix={<UserOutlined />}
                            valueStyle={{ color: '#52c41a' }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card bordered={false} className="analytics-card">
                        <Statistic
                            title="Active Days"
                            value={analytics.dailyStats?.length}
                            prefix={<LineChartOutlined />}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card bordered={false} className="analytics-card">
                        <Statistic
                            title="Avg Events/Learner"
                            value={course.stats?.total_learners > 0 ? (course.stats.total_events / course.stats.total_learners).toFixed(1) : 0}
                        />
                    </Card>
                </Col>
            </Row>

            <Row gutter={[24, 24]}>
                {/* Daily Activity */}
                <Col xs={24} lg={16}>
                    <Card title={<Space><LineChartOutlined /> Daily Activity Timeline</Space>} bordered={false}>
                        <div style={{ padding: '0 0 24px 0' }}>
                            <ActivitySparkline data={analytics.dailyStats} />
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, color: '#888', fontSize: 12 }}>
                                <span>{analytics.dailyStats[0]?.activity_date}</span>
                                <span>Total events per day</span>
                                <span>{analytics.dailyStats[analytics.dailyStats.length - 1]?.activity_date}</span>
                            </div>
                        </div>
                        <Table
                            size="small"
                            dataSource={analytics.dailyStats.slice().reverse().slice(0, 10)}
                            rowKey="activity_date"
                            pagination={false}
                            columns={[
                                { title: 'Date', dataIndex: 'activity_date', key: 'date' },
                                { title: 'Video', dataIndex: 'video', key: 'video' },
                                { title: 'Problem', dataIndex: 'problem', key: 'problem' },
                                { title: 'Forum', dataIndex: 'forum', key: 'forum' },
                                { title: 'Total', dataIndex: 'total', key: 'total', render: (v) => <Text strong>{v}</Text> },
                            ]}
                        />
                    </Card>
                </Col>

                {/* Category Breakdown */}
                <Col xs={24} lg={8}>
                    <Card title={<Space><BarChartOutlined /> Interaction Categories</Space>} bordered={false}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
                            {analytics.categoryStats.map(item => (
                                <div key={item.event_category}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                                        <Space>
                                            {getCategoryIcon(item.event_category)}
                                            <Text strong style={{ textTransform: 'capitalize' }}>{item.event_category}</Text>
                                        </Space>
                                        <Text type="secondary">{parseInt(item.count).toLocaleString()}</Text>
                                    </div>
                                    <Progress
                                        percent={Math.round((parseInt(item.count) / course.stats.total_events) * 100)}
                                        showInfo={true}
                                        size="small"
                                        strokeColor={
                                            item.event_category === 'video' ? '#1890ff' :
                                                item.event_category === 'problem' ? '#52c41a' :
                                                    item.event_category === 'forum' ? '#faad14' :
                                                        '#d9d9d9'
                                        }
                                    />
                                </div>
                            ))}
                        </div>
                    </Card>
                </Col>

                {/* Top Learners — lazy loaded */}
                <Col span={24}>
                    <Card
                        title={<Space><UserOutlined /> Active Learners Summary (Top 100)</Space>}
                        bordered={false}
                        extra={
                            <Button type="primary" onClick={() => navigate(`/courses/${courseKey}/learners`)} data-export-hide>
                                View Full Interaction Details
                            </Button>
                        }
                    >
                        <Table
                            dataSource={topLearners.slice(0, 10)}
                            columns={learnerColumns}
                            rowKey="username"
                            pagination={false}
                            loading={learnersLoading}
                        />
                    </Card>
                </Col>
            </Row>

            <style dangerouslySetInnerHTML={{
                __html: `
        .analytics-card {
          box-shadow: 0 4px 12px rgba(0,0,0,0.05);
          transition: transform 0.3s;
        }
        .analytics-card:hover {
          transform: translateY(-5px);
        }
      `}} />
        </div>
    );
}
